/**
 * Data-driven enemy identity — tags/stats from enemies.json.
 * Judgment code must use tags / helpers here; do not branch on defId strings.
 */

import { raidCatalog } from "../../data/raid-catalog";
import enemiesJson from "../../data/tables/raid/enemies.json";
import type { RaidUnit } from "./raid-types";

export const RaidEnemyDefId = {
  Add: "add",
  Charger: "charger",
  Brute: "brute",
} as const;
export type RaidEnemyDefId =
  (typeof RaidEnemyDefId)[keyof typeof RaidEnemyDefId];

export const RaidEnemyTag = {
  PackMember: "packMember",
  PackChew: "packChew",
  Elite: "elite",
  ChargeCaster: "chargeCaster",
  ChargeLethal: "chargeLethal",
  ArmAfterDrop: "armAfterDrop",
  WeaponReachPeel: "weaponReachPeel",
} as const;
export type RaidEnemyTag =
  (typeof RaidEnemyTag)[keyof typeof RaidEnemyTag];

export const RaidZoneKind = {
  Skill: "skill",
  PackAa: "packAa",
} as const;
export type RaidZoneKind =
  (typeof RaidZoneKind)[keyof typeof RaidZoneKind];

export const RaidSkillId = {
  Slam: "slam",
  Brass: "brass",
  Leap: "leap",
  Charge: "charge",
} as const;
export type RaidSkillId = (typeof RaidSkillId)[keyof typeof RaidSkillId];

type SoftWeightKey = "enemyMelee" | "enemyBruteMelee";

type EnemyRow = {
  tags: readonly string[];
  meleeRadiusPad: number;
  softWeightKey: SoftWeightKey;
  engageScoreBonus: number;
  weaponReachPeelBias: number;
};

type EnemiesTable = {
  packAaDanger: number;
  byDefId: Record<string, EnemyRow>;
};

function enemiesTable(): EnemiesTable {
  return (
    (raidCatalog as { enemies?: EnemiesTable }).enemies ??
    (enemiesJson as EnemiesTable)
  );
}

export function enemyRowOf(
  defId: string | undefined | null,
): EnemyRow | null {
  if (!defId) return null;
  return enemiesTable().byDefId[defId] ?? null;
}

export function enemyTagsOf(
  u: Pick<RaidUnit, "defId"> | string,
): readonly string[] {
  const id = typeof u === "string" ? u : u.defId;
  return enemyRowOf(id)?.tags ?? [];
}

export function enemyHasTag(
  u: Pick<RaidUnit, "defId"> | string,
  tag: RaidEnemyTag | string,
): boolean {
  return enemyTagsOf(u).includes(tag);
}

/** Living team-1 hostile that judgment may consider (skip unarmed drop units). */
export function enemyIsCombatRelevant(
  e: Pick<RaidUnit, "team" | "hp" | "defId" | "combatArmed">,
): boolean {
  if (e.team !== 1 || e.hp <= 0) return false;
  if (enemyHasTag(e, RaidEnemyTag.ArmAfterDrop) && !e.combatArmed) {
    return false;
  }
  return true;
}

export function enemyMeleeRadiusPad(e: Pick<RaidUnit, "defId">): number {
  const row = enemyRowOf(e.defId);
  if (row) return row.meleeRadiusPad;
  return 4;
}

export function enemySoftWeightKey(
  e: Pick<RaidUnit, "defId">,
): SoftWeightKey {
  return enemyRowOf(e.defId)?.softWeightKey ?? "enemyMelee";
}

export function enemyEngageScoreBonus(e: Pick<RaidUnit, "defId">): number {
  return enemyRowOf(e.defId)?.engageScoreBonus ?? 0;
}

export function enemyWeaponReachPeelBias(
  e: Pick<RaidUnit, "defId">,
): number {
  return enemyRowOf(e.defId)?.weaponReachPeelBias ?? 0;
}

export function raidPackAaDanger(): number {
  const n = Number(enemiesTable().packAaDanger);
  return Number.isFinite(n) ? n : 20;
}
